import React, { useState } from 'react';
import { ChevronRight, Sparkles } from 'lucide-react';
import { detectDeviceLanguage } from '../../contexts/LanguageContext';

const IMAGES = [
  'https://static.prod-images.emergentagent.com/jobs/0932821b-686a-4273-8841-24ce4eba86f2/images/a5b95caab72e2d3e2fd73db4f64e01486fe762a69894255ab52a02d82bb1303f.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/0932821b-686a-4273-8841-24ce4eba86f2/images/55495fe81ae200d7cc99ab96a3d653beb4a5f4887d0044205eaf7be3c530c6a4.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/0932821b-686a-4273-8841-24ce4eba86f2/images/d9096eff10fe72489ffc02d5e88771884cce3aa6f7ad30d17c348026b047eb35.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/0932821b-686a-4273-8841-24ce4eba86f2/images/acec4e2321a76e361c73ccf0b136968ffaf9cf162544a74444318c7ed2210780.jpeg'
];

const CONTENT = {
  'fr-FR': {
    skip: 'Passer',
    cta: "Commencer l'aventure",
    slides: [
      { badge: 'Bienvenue', title: 'La magie du soir commence ici', text: "123zzz crée des histoires du soir uniques et enchantées, pensées pour émerveiller votre enfant chaque nuit." },
      { badge: 'Personnalisation', title: 'Votre enfant devient le héros', text: "Choisissez le prénom, le thème et la langue : chaque histoire est écrite sur mesure, rien que pour lui." },
      { badge: 'Illustrations', title: 'Des images qui font rêver', text: "Chaque histoire prend vie avec de magnifiques illustrations générées par magie, comme un vrai livre." },
      { badge: 'Bibliothèque', title: 'Vos histoires, pour toujours', text: "Retrouvez toutes vos créations dans votre bibliothèque magique, à relire et partager à volonté." }
    ]
  },
  'en-US': {
    skip: 'Skip',
    cta: 'Start the adventure',
    slides: [
      { badge: 'Welcome', title: 'Bedtime magic starts here', text: '123zzz creates unique, enchanted bedtime stories designed to amaze your child every single night.' },
      { badge: 'Personalization', title: 'Your child becomes the hero', text: 'Pick the name, theme and language: every story is written just for them, and no one else.' },
      { badge: 'Illustrations', title: 'Pictures made to dream', text: 'Every story comes to life with beautiful, magically generated illustrations, just like a real book.' },
      { badge: 'Library', title: 'Your stories, forever', text: 'Find all your creations in your magical library, ready to read again and share anytime.' }
    ]
  }
};

const OnboardingSlides = ({ onComplete }) => {
  const [index, setIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const lang = detectDeviceLanguage();
  const { skip, cta, slides } = CONTENT[lang];
  const isLast = index === slides.length - 1;
  const slide = slides[index];

  const next = () => {
    if (isLast) { onComplete(); return; }
    setIndex(i => i + 1);
    setAnimKey(k => k + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 flex items-center justify-center p-4" data-testid="onboarding-container">
      <style>{`
        @keyframes ob-fade-up { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ob-zoom { from { transform: scale(1.08); } to { transform: scale(1); } }
        .ob-img { animation: ob-zoom 6s ease-out forwards; }
        .ob-t1 { animation: ob-fade-up .5s ease-out both; }
        .ob-t2 { animation: ob-fade-up .5s .12s ease-out both; }
        .ob-t3 { animation: ob-fade-up .5s .24s ease-out both; }
      `}</style>

      <div className="relative w-full max-w-md h-[640px] max-h-[88vh] rounded-[2rem] overflow-hidden shadow-2xl bg-indigo-950">
        <img
          key={`img-${animKey}`}
          src={IMAGES[index]}
          alt={slide.title}
          className="ob-img absolute inset-0 w-full h-full object-cover"
        />
        <div className="hidden">
          {IMAGES.map(src => <img key={src} src={src} alt="" />)}
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/40 to-transparent" />

        <button
          onClick={onComplete}
          data-testid="onboarding-skip-button"
          className="absolute top-5 right-5 px-4 py-1.5 rounded-full text-xs font-semibold text-white/90 bg-white/15 backdrop-blur-md border border-white/20 hover:bg-white/25 transition-colors duration-200"
        >
          {skip}
        </button>

        <div key={`txt-${animKey}`} className="absolute bottom-0 inset-x-0 p-7 pb-8">
          <span className="ob-t1 inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full text-[11px] font-bold uppercase tracking-wider text-pink-100 bg-white/15 backdrop-blur-md border border-white/20">
            <Sparkles className="w-3 h-3" />
            {slide.badge}
          </span>
          <h2 className="ob-t2 text-3xl font-extrabold text-white leading-tight mb-3" data-testid="onboarding-slide-title">
            {slide.title}
          </h2>
          <p className="ob-t3 text-sm text-purple-100/90 leading-relaxed mb-7">
            {slide.text}
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2" data-testid="onboarding-dots">
              {slides.map((_, i) => (
                <div
                  key={i}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === index ? 'w-7 bg-white' : 'w-2 bg-white/35'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              data-testid="onboarding-next-button"
              className={`group flex items-center gap-2 font-bold text-white rounded-full shadow-lg transition-all duration-200 hover:shadow-pink-500/40 hover:scale-[1.03] active:scale-95 bg-gradient-to-r from-purple-500 to-pink-500 ${
                isLast ? 'px-6 py-3 text-sm' : 'w-12 h-12 justify-center'
              }`}
            >
              {isLast ? (
                <>{cta} <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" /></>
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSlides;
